import { Go, Table, TriageFlow, type GuideSection } from './parts'

/**
 * Guide utilisateur — version anglaise.
 *
 * Jumeau de `sections.fr.tsx` : mêmes `id`, même ordre, même découpage en
 * sections. Une section ajoutée d'un côté doit l'être de l'autre.
 *
 * Le vocabulaire est celui de la conformité : « merchant », « collector »,
 * « false positive », « evidence file ». Un terme approximatif ici se retrouve
 * dans un courrier à un régulateur.
 */
export const SECTIONS_EN: GuideSection[] = [
  {
    id: 'a-quoi-sert',
    titleKey: 'guide.s.purpose',
    body: (
      <>
        <p>
          The Compliance Crawler watches, continuously, who uses InTouch payment methods and how. It visits websites,
          keeps screenshots of them, scores them, and puts anything that deserves a human eye into a work queue. It
          decides nothing on its own: it prepares the file, you rule on it.
        </p>
        <Table
          head={['Area', 'The question it answers']}
          rows={[
            [<Go to="merchants">Merchants</Go>, 'Are our partner merchants still doing what they declared?'],
            [<Go to="discovery">Brand impersonation</Go>, 'Who is registering domains that imitate InTouch, Wave, Orange Money or another operator?'],
            [<Go to="webscan">Undeclared merchants</Go>, 'Who is collecting money with our payment methods without being authorised for that brand?'],
            [<Go to="ads">Ad detection</Go>, 'Who advertises, for what, and with what tax status?'],
            [<Go to="betting">Betting & gambling</Go>, 'Do betting websites accept payments where it is prohibited?'],
          ]}
        />
        <p>
          This guide is for everyone who opens the tool: the agent who triages detections day to day, the manager who
          launches scans and configures the search vocabulary, the administrator who manages accounts. Each section
          says who can do what.
        </p>
      </>
    ),
  },
  {
    id: 'premiers-pas',
    titleKey: 'guide.s.start',
    body: (
      <>
        <p>
          There is no sign-up: an administrator creates your account and gives you your email address and a password.
        </p>
        <p>
          <strong>Changing your password.</strong> No screen allows it today. Ask an administrator, who resets it from{' '}
          <Go to="users">Users</Go>. Do not share your password even so — simply say that it needs changing.
        </p>
        <p>
          <strong>If you can see almost nothing</strong>, it is not a fault, it is your scope. Two settings, placed on
          your account by an administrator, decide everything that follows:
        </p>
        <ul>
          <li>
            <strong>countries</strong> — you only see detections from the countries assigned to you. The message “No
            country is associated with your account” means your account shares none with your organisation;
          </li>
          <li>
            <strong>modules</strong> — each menu entry corresponds to a module. A module missing from your account, or
            one your organisation has not subscribed to, does not appear at all.
          </li>
        </ul>
        <p className="guide-callout">
          In both cases, an empty list means “everything the organisation allows”, not “nothing”.
        </p>
        <p>
          <strong>At the bottom of the menu</strong>: the language and theme choices, your name with your role, and
          sign-out. The menu itself collapses to save space.
        </p>
      </>
    ),
  },
  {
    id: 'le-menu',
    titleKey: 'guide.s.menu',
    body: (
      <>
        <p>The side menu is grouped into four sections. Entries marked with a triangle expand into sub-screens.</p>
        <Table
          head={['Entry', 'What you find there', 'Who sees it']}
          rows={[
            [<Go to="overview">Overview</Go>, 'The four figures of the day, two charts, the queue state', 'Merchants module'],
            [<Go to="merchants">Merchants</Go>, 'Portfolio · Crawl reports', 'Merchants module'],
            [<Go to="discovery">Brand impersonation</Go>, 'Domains, apps and accounts imitating a brand', 'Impersonation module'],
            [<Go to="webscan">Undeclared merchants</Go>, 'The worldwide scan and its map', 'Worldwide scan module'],
            [<Go to="ads">Ad detection</Go>, 'Advertisers · Banners · Campaigns · Queries', 'Advertising module'],
            [<Go to="betting">Betting & gambling</Go>, 'Payment tests · Services · Search · To qualify · Reference data', 'Betting module'],
            [<Go to="brands">Search templates</Go>, 'The monitored brands and their vocabulary', 'Brands module'],
            [<Go to="rules">Country rules</Go>, 'What is prohibited, restricted or flagged, country by country', 'Rules module'],
            [<Go to="users">Users</Go>, 'The accounts and their scopes', 'administrators'],
            [<Go to="orgs">Organisations</Go>, 'Customer organisations and their subscriptions', 'super admin'],
          ]}
        />
        <p>
          Three entries carry a counter when work is waiting: <strong>Advertisers</strong>,{' '}
          <strong>Payment tests</strong> and <strong>To qualify</strong>. They are the best place to start in the
          morning.
        </p>
        <p>
          One useful nuance: module filtering applies to agents only. An administrator, an owner or a super admin sees
          every menu entry. The organisation’s subscription, on the other hand, binds everyone except the super admin.
        </p>
      </>
    ),
  },
  {
    id: 'vue-d-ensemble',
    titleKey: 'guide.s.overview',
    body: (
      <>
        <p>This is the home screen, and it answers a single question: what is waiting for someone today?</p>
        <p>
          <strong>The top strip</strong> counts what is open. Four clickable figures leading straight to the relevant
          queue: open alerts, suspended merchants, impersonations to qualify, undeclared merchants to qualify.
        </p>
        <p>
          <strong>Three health indicators</strong> complete the strip — monitored merchants, crawls over the last
          24 hours, detections handled. They call for no action: they say whether the machine is running.
        </p>
        <p>
          <strong>Two charts.</strong> “Alerts by level” stacks the OK, WARNING and CRITICAL levels day by day — that
          is where a drift shows before it becomes a problem. “Breakdown by action” shows what the crawls recommended.
        </p>
        <p>
          <strong>The queue bar</strong>, at the bottom, is the technical state of the jobs: waiting, active, completed,
          failed, delayed. A rising number of failures should go to the technical team — it is not something you can
          fix from the interface.
        </p>
      </>
    ),
  },
  {
    id: 'marchands',
    titleKey: 'guide.s.merchants',
    body: (
      <>
        <h3>The portfolio</h3>
        <p>
          The table sorts by any column. Four statuses: <strong>Active</strong>, <strong>Suspended</strong>,{' '}
          <strong>Under review</strong>, <strong>Terminated</strong>.
        </p>
        <p>
          <strong>Adding a merchant</strong> takes five fields: name, URL, country, declared category and crawl
          frequency. The category matters a great deal: it is what gets compared against what the tool actually finds
          on the website. A merchant declared as “retail” whose page sells bets will be flagged for that discrepancy.
        </p>
        <p>
          <strong>CSV import</strong> loads several at once. Rows are previewed before anything is written.
        </p>
        <Table
          head={['Action', 'Effect', 'Who can']}
          rows={[
            ['Scan', 'Starts a crawl at once, with a progress bar', 'administrator'],
            ['Edit', 'Changes the declared information', 'everyone'],
            ['Suspend', 'Moves an active merchant to suspended', 'everyone'],
            ['Reactivate', 'Moves a suspended merchant back to active', 'everyone'],
            ['Delete', 'Erases the merchant, after confirmation. Irreversible', 'administrator'],
          ]}
        />
        <h3>Crawl reports</h3>
        <p>
          Every visit leaves a report, filterable by level, country and action. The score runs from 0 to 100 and sets
          the level:
        </p>
        <Table
          head={['Score', 'Level', 'What to do']}
          rows={[
            ['under 40', 'OK', 'Nothing. The visit is compliant'],
            ['40 to 69', 'WARNING', 'To review'],
            ['70 and above', 'CRITICAL', 'Handle without delay — suspension, blocking or takedown'],
          ]}
        />
        <p>
          <strong>Opening a report</strong> gives everything the score was built on: the sub-scores, the AI’s analysis
          of the image, the reasons one by one, the payment keywords, the screenshot of the website at the time of the
          visit, and an extract of the visible text.
        </p>
        <p>
          Two indicators deserve an explanation. <strong>SSL</strong> says whether the certificate was valid. The{' '}
          <strong>drift score</strong> measures the distance between today’s page and the reference page: close to 1,
          the website has not moved; below 0.4, it has changed in nature and the risk score rises sharply. That is the
          signal of a website resold or hijacked.
        </p>
      </>
    ),
  },
  {
    id: 'usurpation',
    titleKey: 'guide.s.impersonation',
    body: (
      <>
        <p>
          This screen gathers everything that imitates a monitored brand. The tool does not search at random: it starts
          from the brands and the vocabulary configured in <Go to="brands">Search templates</Go>.
        </p>
        <Table
          head={['Tab', 'Source', 'What it spots']}
          rows={[
            ['Domains', 'Public SSL certificate logs', 'A freshly registered domain with a name close to a brand. The earliest source: often seen before the website is live'],
            ['Apps', 'Google Play and the App Store', 'A fake app carrying a brand’s name'],
            ['Social', 'Web search and public Telegram groups', 'A fake account presenting itself as official customer support'],
            ['SMS', 'Reports received from users', 'A fraudulent SMS, with the link it contained'],
            ['Payments', 'Payment pages analysed', 'A suspicious payment integration'],
          ]}
        />
        <p>
          <strong>Reading a record.</strong> Each detection carries the brand imitated, how the resemblance was
          established, a similarity score and a suspicion level — LOW, MEDIUM, HIGH or CRITICAL. CRITICAL records are
          outlined in red. The level is not a decision: it is a reading priority.
        </p>
        <p>
          Resemblance is established in four ways: the brand name is <strong>contained</strong> in the domain, it is a
          close <strong>typo</strong>, they are <strong>look-alike characters</strong> from another alphabet, or it is
          the brand <strong>joined to another word</strong>, as in “wave-money-sn”.
        </p>
        <p>
          <strong>Checking a domain by hand.</strong> One field lets you paste an address and compare it against the
          suspicious domains already known. Handy when someone reports a link to you.
        </p>
        <p>
          <strong>Launching a scan</strong> is restricted to administrators. The scans run on their own in any case:
          domains every hour, app stores and social media twice a day.
        </p>
      </>
    ),
  },
  {
    id: 'non-declares',
    titleKey: 'guide.s.undeclared',
    body: (
      <>
        <p>
          Here we are not looking for who imitates the brand, but for <strong>who collects money with our payment
          methods without being authorised to</strong>. The tool queries the web country by country with phrasings such
          as “pay with InTouch”, visits the websites it finds, and compares them against your records.
        </p>
        <Table
          head={['Verdict', 'Situation', 'Suggested action']}
          rows={[
            ['To onboard', 'Website unknown to the records, legitimate activity', 'Approach it, or block it if the activity is prohibited'],
            ['To investigate', 'Known merchant, but not authorised for this brand — authorised for Wave, say, and found collecting through InTouch', 'Look for the missing contract'],
            ['To block', 'Unknown website and activity prohibited in that country', 'Cut off collection'],
          ]}
        />
        <p>A merchant that is known <strong>and</strong> authorised for the brand does not appear here: merchant monitoring already covers it.</p>
        <p>
          <strong>The map.</strong> The world map colours each country by the number of undeclared merchants found: the
          darker the country, the more there are. Very light grey, the country is not monitored; grey, it is monitored
          but nothing was found. Click a country to filter the list.
        </p>
        <p className="guide-callout guide-callout-warn">
          <strong>Read the warning banner at the top, if it appears.</strong> If it says that no merchant × brand
          authorisation is recorded, the cross-check is inert: <strong>everything</strong> comes back as unknown, your
          own merchants included. The results are then leads to qualify, not breaches established. The answer is not to
          work through the list, but to complete the authorisations in <Go to="brands">Search templates</Go>.
        </p>
        <p>The scan runs once a day, around 3 a.m., on a different country each day in rotation.</p>
      </>
    ),
  },
  {
    id: 'pubs',
    titleKey: 'guide.s.ads',
    body: (
      <>
        <p>
          This module records who advertises in your countries, and sets those advertisers against their tax standing.
        </p>
        <h3>Advertisers</h3>
        <p>Each advertiser carries <strong>two independent statuses</strong>, and the distinction is essential:</p>
        <ul>
          <li>
            <strong>the tax status</strong> describes the business: <em>Not checked</em>, <em>Registered</em>,{' '}
            <em>Not registered</em>, <em>Being checked</em>. It is a fact to establish;
          </li>
          <li>
            <strong>the review status</strong> describes your work on the file. It is progress.
          </li>
        </ul>
        <p>
          An advertiser that is <em>Not registered</em> for tax and <em>New</em> in review is a hot file nobody has
          opened yet. The menu counter counts them.
        </p>
        <h3>Banners on websites</h3>
        <p>
          Unlike advertisers, which are found by search, banners are spotted by visiting a list of target websites —
          news, forums, classifieds, e-commerce, blogs, portals. You manage that list right here, one at a time or by
          import.
        </p>
        <h3>Campaigns and queries</h3>
        <p>
          <strong>Scan campaigns</strong> is the history: what was searched, when, in which country, and what it
          returned.
        </p>
        <p>
          <strong>Monitored queries</strong> is the list of phrasings searched, grouped by sector. Three pages of
          results are read per query and per country. The <strong>Gambling</strong> sector is marked as sensitive: a
          regulated activity, to look at first.
        </p>
        <p>
          Editing the queries is restricted to administrators. It is the setting with the greatest effect on what the
          tool finds, and it consumes search quota: a query added lightly costs searches to all the others.
        </p>
        <h3>Three platforms</h3>
        <p>
          Results state where they came from — <strong>Google Ads</strong>, <strong>Meta</strong> or{' '}
          <strong>Banners</strong> — and whether it was a paid ad, an organic result or a banner. Meta collection goes
          through a third-party provider; if it is not configured on the server, a banner says so and only Meta
          collection is missing.
        </p>
      </>
    ),
  },
  {
    id: 'paris',
    titleKey: 'guide.s.betting',
    body: (
      <>
        <p>
          The richest module, because the question is the most delicate: does a betting website accept a payment where
          the law prohibits it? You cannot answer that from a desk: you have to go to the end of the payment journey.
        </p>
        <h3>Payment tests</h3>
        <p className="guide-callout guide-callout-warn">
          <strong>The test stops before the payment.</strong> Two depths only: up to the choice of payment method, or
          up to the payment request — <strong>before the PIN is entered</strong>. No test completes a transaction.
          Faced with a PIN screen, the test is over: note what you see and stop there.
        </p>
        <p>
          Each test carries a number and says whether it was run by the <strong>crawler</strong> or by an{' '}
          <strong>agent</strong>. Its course goes through five states: pending, journey running, journey complete,
          journey blocked, journey failed. A blocked journey is not a failure on your part — it is usually the website
          refusing the traffic.
        </p>
        <p>
          Along the journey, you record the steps, the addresses visited, the payment methods offered, the aggregators
          met, the third-party domains, the payee’s name, the amount, the text of the SMS received. You can attach
          evidence. A <strong>PDF ticket</strong> can be generated at any time: it is the document to put in the
          evidence file.
        </p>
        <p><strong>Qualifying the result</strong> happens in two stages. First the nature of the breach:</p>
        <Table
          head={['Breach', 'What it means']}
          rows={[
            ['Unauthorised aggregator', 'Collection goes through an intermediary with no agreement'],
            ['Service prohibited in the country', 'The activity itself is prohibited where the test was run'],
            ['Unlicensed operator', 'The website operates without a gambling licence'],
            ['Payment method used without authorisation', 'Our brand is accepted with no contract for that use'],
            ['Non-compliant advertising', 'The breach is in the communication'],
            ['Technical issue', 'The test did not complete for a material reason'],
          ]}
        />
        <p>
          Then the severity: <em>High</em>, <em>Medium</em>, <em>Low</em>, <em>Technical issue</em>, or{' '}
          <em>Compliant</em>. A test not yet qualified shows as “To qualify”.
        </p>
        <h3>Finding websites</h3>
        <p><strong>Two sources, of very different cost</strong>:</p>
        <ul>
          <li><strong>the web search</strong> consumes the monthly search quota, one search per template sent;</li>
          <li><strong>the ads</strong> re-read the advertisers already collected and cost nothing.</li>
        </ul>
        <p>
          The screen states what a pass will spend <strong>before</strong> you launch it, and what it spent afterwards.
          Look at that figure: the quota is monthly and empties fast.
        </p>
        <p>
          A search writes nothing to the database. It returns candidates, which you then import in two stages — a plan
          first, which you confirm. Nothing is created behind your back.
        </p>
        <h3>To qualify</h3>
        <p>
          The tool’s learning queue: the payees and domains seen during tests that the reference data does not
          recognise yet. Each decision enriches the reference data, and the next test will recognise what you have just
          named. A quarter of an hour spent here pays for itself across every test that follows.
        </p>
        <h3>Services and reference data</h3>
        <p>
          <strong>Betting services</strong> is the list of websites monitored, with their test accounts. Managing the
          accounts is restricted to administrators, viewing included — these are credentials.
        </p>
        <p>
          <strong>Reference data</strong> gathers the mobile carriers and the payment aggregators, each with a status:{' '}
          <em>Authorised</em>, <em>Not authorised</em>, or <em>To qualify</em>. An aggregator left at “To qualify” will
          never raise a breach: that is where the real reach of the monitoring is decided.
        </p>
      </>
    ),
  },
  {
    id: 'traiter-une-detection',
    titleKey: 'guide.s.triage',
    body: (
      <>
        <p>
          The three queues — brand impersonation, undeclared merchants, betting & gambling — share{' '}
          <strong>the same path</strong>. That is deliberate: you do not have to relearn a set of statuses on every
          tab.
        </p>
        <TriageFlow />
        <p>
          A <em>New</em> case can also be taken on directly, or set aside at once when the error is plain.
        </p>
        <p>
          <strong>A reason is required to close.</strong> The four tinted states on the diagram do not accept a move
          without a written comment. This is not a formality: it is that comment which, in six months, will explain the
          decision to someone who was not there. Write what you found, not what you did.
        </p>
        <p>
          <strong>Jumps are refused.</strong> You cannot go from <em>New</em> to <em>Archived</em>. If a transition is
          refused, it is not a bug: the intermediate step is missing.
        </p>
        <p>
          <strong>Assignment.</strong> You can hand a detection to someone, or put it back in the shared queue without
          changing its status. Two things to know: the field is free text, so{' '}
          <strong>a typo in a name will not be flagged</strong>, and assignment{' '}
          <strong>notifies nobody</strong>. Tell the person yourself.
        </p>
        <p>
          <strong>Bulk triage</strong> applies one status to several rows at once, up to 500. Each row still keeps its
          own entry in the log.
        </p>
        <p>
          <strong>The log.</strong> Every detection carries the history of its triage: starting state, ending state,
          reason, author, timestamp. Nothing in it is ever rewritten or erased, and the author is taken from your
          session — you cannot act under someone else’s name. It is that trail which makes a file hold up.
        </p>
        <p>
          <strong>Overdue.</strong> A detection open for more than <strong>15 days</strong> counts as overdue. Each
          queue’s summary shows that figure, along with the number of rows nobody has taken on.
        </p>
        <p className="guide-callout">
          <strong>Every role can do all of this</strong>, agents included. Confirming a breach, escalating it or
          archiving it requires no particular rank today. If your organisation wants to reserve those decisions for a
          manager, that is a rule to keep among yourselves — the tool will not enforce it.
        </p>
      </>
    ),
  },
  {
    id: 'configuration',
    titleKey: 'guide.s.configuration',
    body: (
      <>
        <h3>Search templates</h3>
        <p>
          This is the setting that governs everything else. The monitored brands are not written into the code: they
          are here, and a brand added this morning is taken into account at the next scan.
        </p>
        <p>
          Each brand belongs to a category — <strong>Internal</strong> for InTouch, <strong>Partner</strong> for an
          operator, <strong>Third-party client</strong> for a brand whose impersonation you monitor on a customer’s
          behalf.
        </p>
        <Table
          head={['Field', 'What it is for']}
          rows={[
            ['Name', 'The readable name, reused in the alerts'],
            ['Search terms', 'The strings searched inside domain names'],
            ['Official domains', 'Your own domains, excluded from the alerts. Forget one here and you flag yourself'],
            ['Known typosquats', 'The variants already seen, on top of those the tool generates on its own'],
            ['Target countries', 'Prioritises the alerts. Empty means “everywhere”'],
            ['Web search templates', 'The phrasings used by the worldwide scan, one per line'],
          ]}
        />
        <p>
          In the web search templates, <code>{'{brand}'}</code> is replaced by the brand name. “pay with
          {' {brand} '}” becomes “pay with InTouch”. Add the phrasings your customers actually use, in their own
          language.
        </p>
        <p>
          Creating, editing or deleting a brand is restricted to the super admin — it is the most structural setting in
          the tool.
        </p>
        <p>
          <strong>Authorised merchants.</strong> Each brand card expands onto the list of merchants authorised for{' '}
          <strong>that brand specifically</strong>, with an optional contract reference. A merchant may be authorised
          for Wave and not for InTouch: that is exactly what the worldwide scan checks. The same link is managed from a
          merchant’s record, on the brands side — the two screens act on the same data.
        </p>
        <h3>Country rules</h3>
        <p>The matrix of what is allowed, activity by activity and country by country. Four levels:</p>
        <ul>
          <li><strong>Allowed</strong> — nothing to report;</li>
          <li><strong>Conditional</strong> — legal with a licence or with mandatory notices;</li>
          <li><strong>Grey area</strong> — not prohibited, but to be watched;</li>
          <li><strong>Prohibited</strong> — the activity is prohibited in that country.</li>
        </ul>
        <p>
          These levels weigh directly on a crawl’s risk score: a <strong>prohibited</strong> activity adds 100 points
          on its own, which puts the report into critical whatever else happens. Editing is restricted to
          administrators, and deserves to be: changing a rule without a documentable legal reference amounts to
          manufacturing a breach, or erasing one.
        </p>
      </>
    ),
  },
  {
    id: 'administration',
    titleKey: 'guide.s.administration',
    body: (
      <>
        <h3>Users</h3>
        <p>
          Visible to administrators only. An account is created with six items: name, email address, initial password,
          role, <strong>authorised countries</strong> and <strong>authorised screens</strong>.
        </p>
        <p className="guide-callout">
          For both lists of tick boxes, <strong>ticking nothing opens everything</strong> the organisation allows. If
          you want to restrict someone to a single country, tick that country — do not leave the list empty thinking it
          restricts.
        </p>
        <p><strong>Three rules govern who may act on whom</strong>, and they apply even to an administrator:</p>
        <ol>
          <li>You only act on an account in <strong>your own organisation</strong>.</li>
          <li>You only act on an account of <strong>strictly lower rank</strong>. An administrator therefore cannot edit another administrator.</li>
          <li>You <strong>cannot delete your own account</strong>.</li>
        </ol>
        <p>
          Only the super admin creates or promotes owners and super admins, and only they can move an account from one
          organisation to another.
        </p>
        <p>
          An account is <strong>deactivated</strong> rather than deleted when the person leaves: the history of their
          decisions stays readable in the logs.
        </p>
        <p>
          <strong>Resetting a password</strong> is done by editing the account. Leave the field empty to leave it
          alone. Since the interface does not yet let everyone change their own, this is currently the only route —
          pass the new password on in person, not in writing.
        </p>
        <h3>Organisations</h3>
        <p>
          Super admin only. An organisation carries its name, a plan, the countries assigned and the modules
          subscribed, with a country ceiling shown as you type. Neither list may be empty.
        </p>
        <p>
          Those two settings are the ceiling for everything else: a user can never see a country or a module their
          organisation does not have. If someone complains about not seeing a screen while their account looks correct,
          check the organisation before the account.
        </p>
      </>
    ),
  },
  {
    id: 'roles',
    titleKey: 'guide.s.roles',
    body: (
      <>
        <p>
          Four roles, but in practice <strong>two levels of rights</strong>: what is open to everyone, and what
          requires being an administrator. Each role adds to the ones before it.
        </p>
        <Table
          head={['Role', 'What it adds']}
          rows={[
            ['Agent', 'View and export everything within scope · run and qualify payment tests · create, edit, suspend and reactivate a merchant · rule on an advertiser · triage detections end to end, assignment and closure included'],
            ['Administrator', 'Launch every scan · manage the search vocabulary and the queries · rule on candidates · manage merchant × brand authorisations · terminate and delete · edit the country rules · access the test accounts · manage the users of their organisation'],
            ['Owner', 'Nothing more in practice. Their higher rank only makes them untouchable by an administrator, and lets them act on their organisation’s administrators'],
            ['Super admin', 'Create and edit the monitored brands · manage the organisations · see every country and every module, with no filter at all'],
          ]}
        />
      </>
    ),
  },
  {
    id: 'avant-de-signaler',
    titleKey: 'guide.s.beforeReporting',
    body: (
      <>
        <p>Six behaviours surprise people often, and none of them is a fault.</p>
        <ol>
          <li><strong>An empty list of tick boxes opens everything.</strong> Countries as well as screens: the organisation’s ceiling is what applies.</li>
          <li><strong>You cannot change your own password.</strong> The screen does not exist yet; go through an administrator.</li>
          <li><strong>Assigning a detection notifies nobody</strong>, and the name you type is not checked.</li>
          <li><strong>The worldwide scan returns everything as “unknown”</strong> as long as no merchant × brand authorisation is recorded. A banner says so.</li>
          <li><strong>An aggregator left at “To qualify” will never raise a breach.</strong> Incomplete reference data makes the monitoring silent, not noisy.</li>
          <li><strong>Searches have a monthly quota.</strong> A scan that returns nothing at the end of the month may simply have exhausted it.</li>
        </ol>
        <p>
          What does deserve reporting to the technical team: a rising number of <strong>failed</strong> jobs on the
          overview, a missing screenshot on a report, or a refused transition that the path diagram nonetheless allows.
        </p>
      </>
    ),
  },
]
